import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Message } from 'semantic-ui-react';
import { useMutation, gql } from '@apollo/client';

import { PATHS } from '../../paths';
import { AuthCard } from '../../components/AuthCard';

import formStyles from '../../components/AuthCard/form.module.css';

/**
 * The operation name matters: `RequestPasswordReset` is in the auth-bypass list in the backend's
 * `server.ts`, so renaming it here makes the request require a token — which nobody asking for a
 * password reset has.
 */
const REQUEST_PASSWORD_RESET = gql`
  mutation RequestPasswordReset($userName: String!) {
    requestPasswordReset(userName: $userName)
  }
`;

export const ForgotPassword = () => {
  const [userName, setUserName] = useState('');
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);

  const [requestReset, { loading }] = useMutation(REQUEST_PASSWORD_RESET);

  const canSubmit = userName.trim().length > 0 && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      await requestReset({ variables: { userName: userName.trim() } });
      setSent(true);
    } catch {
      // The mutation returns true whether or not the account exists, so the only way here is a
      // genuine network or server failure — worth telling them, because retrying may work.
      setFailed(true);
    }
  };

  /**
   * Deliberately identical whether or not that address has an account. The server answers the
   * same way for the same reason: varying the response turns this form into a way of discovering
   * who holds an account.
   */
  if (sent) {
    return (
      <AuthCard title="Check your email">
        <Message success>
          <p>
            If an account exists for <strong>{userName.trim()}</strong>, we've sent it a link to
            reset the password.
          </p>
          <p>The link works once and expires in 24 hours.</p>
        </Message>
        <p className={formStyles.switchLink}>
          Nothing after a few minutes? Check your spam folder, or{' '}
          <a
            href="#retry"
            onClick={(e) => {
              e.preventDefault();
              setSent(false);
            }}
          >
            try a different address
          </a>
          .
        </p>
        <p className={formStyles.switchLink}>
          <Link to={PATHS.LOGIN}>Back to login</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Reset your password">
      <p style={{ marginBottom: '1em', color: '#666' }}>
        Enter the email address you log in with and we'll send you a link to choose a new
        password.
      </p>
      <form onSubmit={handleSubmit}>
        <div className={formStyles.field}>
          <Input
            fluid
            icon="user"
            iconPosition="left"
            placeholder="Email address"
            name="username"
            type="email"
            autoComplete="username"
            value={userName}
            onChange={(e) => {
              setUserName(e.target.value);
              setFailed(false);
            }}
          />
        </div>

        {failed && (
          <Message
            error
            header="We couldn't send that"
            content="Something went wrong on our end. Please try again in a moment."
          />
        )}

        <Button
          type="submit"
          fluid
          size="large"
          className={formStyles.submitBtn}
          loading={loading}
          disabled={!canSubmit}
        >
          Send reset link
        </Button>
      </form>
      <p className={formStyles.switchLink}>
        Remembered it? <Link to={PATHS.LOGIN}>Log in</Link>
      </p>
    </AuthCard>
  );
};
